import type { TopLocality } from "@/hooks/use-top-localities";
import { encodeLocation } from "@/utils/search-url-transform/encode-decode-location";

interface PropertyType {
	value: string;
	label: string;
}

export interface PropertyLink {
	label: string;
	href: string;
}

// Property types similar to the original Footer.js
const PROPERTY_TYPES = {
	apartment: { value: "apartment", label: "Apartment" },
	house: { value: "house", label: "House" },
	villa: { value: "villa", label: "Villa" },
	apartment2bhk: { value: "apartment", label: "Apartment" },
	apartment3bhk: { value: "apartment", label: "Apartment" },
} as const;

// Cache for URL construction to avoid repeated string operations
const urlCache = new Map<string, string>();

/**
 * Build search URL using new city/slug route structure
 */
function buildSearchUrl(
	propertyType: string,
	bedrooms: string | undefined,
	localityCode: string,
	localityName: string,
	maxBudget?: string,
): string {
	// Create cache key for memoization
	const cacheKey = `${propertyType}-${bedrooms || "none"}-${localityCode}-${maxBudget || "none"}`;

	if (urlCache.has(cacheKey)) {
		return urlCache.get(cacheKey)!;
	}

	// Default city (hardcoded to bengaluru for now, can be made configurable later)
	const citySlug = "bengaluru";
	const localitySlug = encodeLocation(localityName);

	// Build slug based on property type and bedrooms
	let slug = "";

	if (bedrooms) {
		slug += `${bedrooms}bhk-`;
	}

	// Add property type (pluralized for better SEO)
	const propertyTypeSlug =
		propertyType === "apartment"
			? "apartments"
			: propertyType === "house"
				? "houses"
				: propertyType === "villa"
					? "villas"
					: propertyType;

	slug += `${propertyTypeSlug}-in-${localitySlug}`;

	// Add budget suffix if provided in the format: budget-min-30L-val-60L-max-60L
	if (maxBudget) {
		const budgetValue = maxBudget.toUpperCase();
		slug += `-budget-min-30L-val-${budgetValue}-max-${budgetValue}`;
	}

	const url = `/${citySlug}/${slug}`;
	urlCache.set(cacheKey, url);

	return url;
}

/**
 * Generate property links for a given property type and localities
 * Updated to use new route structure
 */
function generatePropertyLinks(
	localities: TopLocality[],
	propertyType: PropertyType,
	bedrooms?: string,
	maxBudget?: string,
	budgetLabel?: string,
): PropertyLink[] {
	// Pre-calculate common values to avoid repeated operations
	const bedroomPrefix = bedrooms ? `${bedrooms}BHK ` : "";
	const propertyValue = propertyType.value;

	// Use map with pre-allocated array for better performance
	const links: PropertyLink[] = new Array(localities.length);

	for (let i = 0; i < localities.length; i++) {
		const locality = localities[i];

		// Build URL with new route structure
		const href = buildSearchUrl(propertyValue, bedrooms, locality.code, locality.name, maxBudget);

		// Build label based on whether budget is specified
		let label: string;
		if (budgetLabel) {
			// Format: "Flats Under 70L in Whitefield"
			const propertyTypeLabel =
				propertyValue === "apartment"
					? "Flats"
					: propertyValue === "house"
						? "Houses"
						: propertyValue === "villa"
							? "Villas"
							: "Properties";
			label = `${propertyTypeLabel} Under ${budgetLabel} in ${locality.name}`;
		} else {
			// Format: "3BHK Apartment In Whitefield" or "Apartment In Whitefield"
			const propertyLabel = propertyType.label;
			label = `${bedroomPrefix}${propertyLabel} In ${locality.name}`;
		}

		links[i] = { label, href };
	}

	return links;
}

// Pre-calculated property type configurations for better performance
const PROPERTY_CONFIGS = [
	{ key: "apartments", type: PROPERTY_TYPES.apartment, bedrooms: undefined },
	{ key: "houses", type: PROPERTY_TYPES.house, bedrooms: undefined },
	{ key: "villas", type: PROPERTY_TYPES.villa, bedrooms: undefined },
	{ key: "apartment2bhk", type: PROPERTY_TYPES.apartment2bhk, bedrooms: "2" },
	{ key: "apartment3bhk", type: PROPERTY_TYPES.apartment3bhk, bedrooms: "3" },
	{
		key: "apartmentsMax60L",
		type: PROPERTY_TYPES.apartment,
		bedrooms: undefined,
		maxBudget: "60l",
		budgetLabel: "60L",
	},
	{
		key: "apartmentsMax70L",
		type: PROPERTY_TYPES.apartment,
		bedrooms: undefined,
		maxBudget: "70l",
		budgetLabel: "70L",
	},
	{
		key: "apartmentsMax80L",
		type: PROPERTY_TYPES.apartment,
		bedrooms: undefined,
		maxBudget: "80l",
		budgetLabel: "80L",
	},
	{
		key: "apartmentsMax90L",
		type: PROPERTY_TYPES.apartment,
		bedrooms: undefined,
		maxBudget: "90l",
		budgetLabel: "90L",
	},
	{ key: "apartmentsMaxCr", type: PROPERTY_TYPES.apartment, bedrooms: undefined, maxBudget: "1cr", budgetLabel: "1Cr" },
] as const;

/**
 * Generate all property link groups for the footer
 * Updated to use new route structure
 */
export function generateAllPropertyLinks(localities: TopLocality[]) {
	// Early return for empty localities to avoid unnecessary processing
	if (!localities || localities.length === 0) {
		return {
			apartments: [],
			houses: [],
			villas: [],
			apartment2bhk: [],
			apartment3bhk: [],
			apartmentsMax60L: [],
			apartmentsMax70L: [],
			apartmentsMax80L: [],
			apartmentsMax90L: [],
			apartmentsMaxCr: [],
		};
	}

	// Use object with pre-allocated structure
	const result: Record<string, PropertyLink[]> = {};

	// Process all configurations in a single loop
	for (const config of PROPERTY_CONFIGS) {
		result[config.key] = generatePropertyLinks(
			localities,
			config.type,
			config.bedrooms,
			"maxBudget" in config ? config.maxBudget : undefined,
			"budgetLabel" in config ? config.budgetLabel : undefined,
		);
	}

	return result as {
		apartments: PropertyLink[];
		houses: PropertyLink[];
		villas: PropertyLink[];
		apartment2bhk: PropertyLink[];
		apartment3bhk: PropertyLink[];
		apartmentsMax60L: PropertyLink[];
		apartmentsMax70L: PropertyLink[];
		apartmentsMax80L: PropertyLink[];
		apartmentsMax90L: PropertyLink[];
		apartmentsMaxCr: PropertyLink[];
	};
}

/**
 * Distribute links across columns for responsive grid layout
 * Optimized version with pre-allocated arrays and efficient distribution
 */
export function distributeLinksAcrossColumns<T>(links: T[], numColumns: number = 4): T[][] {
	// Early return for empty links
	if (!links || links.length === 0) {
		return Array.from({ length: numColumns }, () => []);
	}

	// Pre-allocate columns array
	const columns: T[][] = new Array(numColumns);
	for (let i = 0; i < numColumns; i++) {
		columns[i] = [];
	}

	// Distribute items efficiently using modulo operation for better balance
	for (let i = 0; i < links.length; i++) {
		const columnIndex = i % numColumns;
		columns[columnIndex].push(links[i]);
	}

	return columns;
}

/**
 * Clear the URL cache - useful for testing or when memory management is needed
 */
export function clearUrlCache(): void {
	urlCache.clear();
}
